/**
 * Custom weekly availability — slot sanity, shared by the write doors and the editor.
 *
 * The 7-day HH:mm grid an offering can carry instead of the general schedule has
 * two ways to be self-contradictory that the wire format CAN see: a range that
 * ends before (or when) it starts, and two ranges on the same day that overlap.
 * Both persist a window the slot generator silently yields nothing from, so both
 * are save errors rather than publish blockers.
 *
 * This is the ONE implementation of that predicate. The service write schemas
 * use it at the API door, and the Availability editors use the same issue list
 * to block Save and point to the exact rows that need attention.
 *
 * A third issue is EDITOR-REACHABLE ONLY: a range with a blank start or end.
 * Time windows are born empty (device smoke 2026-08-13 — a window must not
 * arrive carrying 9-to-5 hours the admin never chose), so an unfinished one has
 * to be nameable. The write schemas never see it, because their HH:mm regex
 * rejects a blank before this refinement runs; it lives here anyway so the ONE
 * issue list drives both the editor's row highlight and its save blocker, which
 * is what keeps the marked row and the refused save in agreement.
 *
 * Deliberately NOT included: "an enabled day with no ranges". The wire format
 * serializes "use the general schedule" and "custom mode with nothing enabled"
 * identically, so only the editor can tell those apart — that rule lives in
 * `isCustomAvailabilitySelectionEmpty` (schemas.ts) and stays editor-only.
 */

export interface CustomAvailabilityDayInput {
  dayOfWeek: number;
  enabled: boolean;
  slots: ReadonlyArray<{ startTime: string; endTime: string }>;
}

export interface CustomAvailabilityIssue {
  /** Position of the day in the submitted grid — the path segment for a Zod issue. */
  dayIndex: number;
  /** 0 = Sunday, 6 = Saturday. */
  dayOfWeek: number;
  /**
   * Position of the offending range within that day's own `slots` array, or
   * null when the issue is about the ENTRY rather than one of its ranges (the
   * duplicate-weekday issue). Callers building a Zod path must branch on it.
   */
  slotIndex: number | null;
  message: string;
}

/**
 * Every end-before-start, same-weekday overlap, and duplicate weekday in the
 * grid, in day order.
 *
 * Ranges are compared as HH:mm strings (lexicographic order is chronological on
 * a zero-padded 24-hour clock, which the wire schema's regex guarantees), and
 * are sorted by start time before the overlap sweep so a day's ranges may be
 * submitted in any order. Disabled days are skipped: their ranges are inert.
 *
 * ⚠ THE SWEEP IS GROUPED BY `dayOfWeek`, NOT BY ARRAY POSITION. The wire format
 * is a LIST of day entries, and nothing in the shape stops two entries from
 * claiming the same weekday. Comparing ranges only within one entry let a
 * caller smuggle an overlap past the door by splitting it across a second
 * Monday entry: `[{dow:1, 09:00-13:00}, {dow:1, 12:00-17:00}]` was accepted and
 * persisted a contradictory Monday. Ranges that land on the same real day are
 * now compared with each other regardless of which entry carried them, and the
 * duplicate entry is itself reported so the editor can collapse it.
 */
export function findCustomAvailabilityIssues(
  days: ReadonlyArray<CustomAvailabilityDayInput> | null | undefined,
): CustomAvailabilityIssue[] {
  const issues: CustomAvailabilityIssue[] = [];
  const issueKeys = new Set<string>();

  const addIssue = (issue: CustomAvailabilityIssue) => {
    const key = `${issue.dayIndex}:${issue.slotIndex ?? 'day'}:${issue.message}`;
    if (issueKeys.has(key)) return;
    issueKeys.add(key);
    issues.push(issue);
  };

  interface GroupedSlot {
    startTime: string;
    endTime: string;
    dayIndex: number;
    slotIndex: number;
  }

  // Group the ENABLED entries by the weekday they actually land on, preserving
  // first-appearance order so the issue list still reads in submission order.
  const weekdayOrder: number[] = [];
  const entriesByWeekday = new Map<number, number[]>();
  const slotsByWeekday = new Map<number, GroupedSlot[]>();

  (days ?? []).forEach((day, dayIndex) => {
    if (!day?.enabled) return;
    const dayOfWeek = day.dayOfWeek;
    if (!entriesByWeekday.has(dayOfWeek)) {
      weekdayOrder.push(dayOfWeek);
      entriesByWeekday.set(dayOfWeek, []);
      slotsByWeekday.set(dayOfWeek, []);
    }
    entriesByWeekday.get(dayOfWeek)!.push(dayIndex);
    const bucket = slotsByWeekday.get(dayOfWeek)!;
    (day.slots ?? []).forEach((slot, slotIndex) => {
      bucket.push({ startTime: slot.startTime, endTime: slot.endTime, dayIndex, slotIndex });
    });
  });

  for (const dayOfWeek of weekdayOrder) {
    // The duplicate itself, reported against every entry after the first.
    const entryIndexes = entriesByWeekday.get(dayOfWeek)!;
    for (const dayIndex of entryIndexes.slice(1)) {
      addIssue({
        dayIndex,
        dayOfWeek,
        slotIndex: null,
        message: 'This day is listed more than once',
      });
    }

    // An UNFINISHED window is reported as itself and then set aside. Comparing a
    // blank as if it were a time is what produced the old nonsense: '' <= ''
    // read as "end before start", so a window the admin had not filled in yet
    // was flagged for a rule it could not be breaking.
    const [incomplete, complete] = partition(
      slotsByWeekday.get(dayOfWeek)!,
      (slot) => slot.startTime.trim() === '' || slot.endTime.trim() === '',
    );

    incomplete.forEach((slot) => {
      addIssue({
        dayIndex: slot.dayIndex,
        dayOfWeek,
        slotIndex: slot.slotIndex,
        message: 'Set a start and end time',
      });
    });

    const ordered = complete
      .slice()
      .sort((a, b) => a.startTime.localeCompare(b.startTime));

    ordered.forEach((slot) => {
      const at = { dayIndex: slot.dayIndex, dayOfWeek, slotIndex: slot.slotIndex };
      if (slot.endTime <= slot.startTime) {
        addIssue({ ...at, message: 'End time must be after start time' });
      }
    });

    ordered.forEach((earlier, earlierIndex) => {
      for (const later of ordered.slice(earlierIndex + 1)) {
        if (later.startTime >= earlier.endTime) break;
        addIssue({
          dayIndex: earlier.dayIndex,
          dayOfWeek,
          slotIndex: earlier.slotIndex,
          message: 'Hours cannot overlap',
        });
        addIssue({
          dayIndex: later.dayIndex,
          dayOfWeek,
          slotIndex: later.slotIndex,
          message: 'Hours cannot overlap',
        });
      }
    });
  }

  return issues;
}

/** Split in one pass, preserving order in both halves. */
function partition<T>(items: readonly T[], matches: (item: T) => boolean): [T[], T[]] {
  const yes: T[] = [];
  const no: T[] = [];
  for (const item of items) {
    (matches(item) ? yes : no).push(item);
  }
  return [yes, no];
}

/** Convenience predicate for callers that only need a yes or no Save gate. */
export function hasCustomAvailabilityIssues(
  days: ReadonlyArray<CustomAvailabilityDayInput> | null | undefined,
): boolean {
  return findCustomAvailabilityIssues(days).length > 0;
}
